// Shared @smartsash/auth client for admin (HS-859, adoption C1; introduced by HS-847 Phase 1b).
//
// It owns the session state machine, sign-in/out, restore, and — via the default
// installHttpHooks — the single 401→refresh policy on the shared api-client instance
// (`customInstance`), which admin never had before.
//
// Why `operations` is still overridden: admin's backend signals "must change password"
// with an HTTP 301 (a status, not a response-body challenge), which the package's default
// sign-in does not model. The calls themselves now go through the generated fetchers, so
// no axios remains on this path.

import {
  getApiAuthMe,
  NormalizedApiError,
  postApiAuthLogin,
  postApiAuthLogout,
  putApiUserChangePassword,
} from '@smartsash/api-client';
import { authErrorFromThrown, createAuth, LOGIN_CHANNEL, type Session } from '@smartsash/auth';

import { tokenManager } from './tokenManager';

import { logger } from '@/shared/logger';

/**
 * A backend that predates the HS-1292 fix answers a temporary-password login with `301` instead
 * of `200`, carrying the same body. Stage already answers `200`; production still runs the old
 * build, so one bundle has to read both. The status is not what the branch is decided on —
 * `requiresPasswordChange` in the body is.
 */
const STATUS_OK = 200;
const STATUS_PASSWORD_CHANGE_REQUIRED = 301;
/** On logout this means the session is already gone — a success, not a failure. */
const STATUS_UNAUTHORIZED = 401;

/**
 * Admin's flow is carried by the cookie, not by a token, so the package's `flowToken` is a label
 * rather than a secret. Exported because the two callers that re-assert the challenge from
 * outside a sign-in — the reload path below and the lab-onboarding flows — must use the same one.
 */
export const PASSWORD_CHANGE_FLOW_TOKEN = 'admin-password-change';

/**
 * `AuthController.LoginUser` sets the token cookies and *then* reports that the password is
 * temporary. Accepting 301 alongside 200 keeps the body readable on the deployment that still
 * answers 301 — axios would otherwise reject before `customInstance` can unwrap the envelope.
 *
 * The flag is read off the generated model either way: the snapshot types the body on the 200,
 * which is the shape both statuses carry.
 */
const LOGIN_REQUEST_OPTIONS = {
  skipAuthRefresh: true,
  // HS-1584: a login 403 means "no admin login permission", not a stale permission snapshot —
  // there is no session for the shared 403 policy to re-read. Same reasoning as the package's own.
  skipForbiddenPolicy: true,
  validateStatus: (status: number) =>
    status === STATUS_OK || status === STATUS_PASSWORD_CHANGE_REQUIRED,
} as const;

/**
 * `skipForbiddenPolicy` breaks the one loop this endpoint can create (HS-1387): the shared 403
 * policy re-reads `/Auth/me`, so a 403 from `/Auth/me` itself would re-read it again, and again.
 * Admin has its own mapper, so it needs its own copy of the opt-out.
 */
const SESSION_READ_OPTIONS = { skipForbiddenPolicy: true } as const;

/**
 * `/Auth/me.roles` (HS-1617) as a string list. TEMPORARY shape check until the client is regenerated
 * with the field: anything that is not an array of strings reads as no roles.
 */
export function reportedRoles(user: object): string[] {
  const roles: unknown = (user as { roles?: unknown }).roles;
  return Array.isArray(roles)
    ? roles.filter((role): role is string => typeof role === 'string')
    : [];
}

/**
 * Map admin's `/Auth/me` payload onto the auth-package `Session`.
 *
 * HS-859: the mapper is now field-complete — it carries `institutionId`, `accountId` and
 * `login` alongside identity. The earlier version dropped `institutionId`, which is why
 * `AuthContext` had to fire a *second* `/Auth/me` (via its own `fetchCurrentUser`) just to
 * obtain it. That duplicate request goes away with the facade.
 *
 * It is not reused from the package because `@smartsash/auth` keeps its equivalent
 * (`toSession` / `restoreFromApi`) module-private — neither is on the barrel, and widening the
 * package API is an explicit non-goal of HS-859. Keeping ONE mapper here, wired into both
 * `signIn` and `restore`, is the admin-scoped equivalent: the resulting session matches the
 * package default field for field.
 */
async function restoreSession(): Promise<Session | null> {
  try {
    const user = await getApiAuthMe(SESSION_READ_OPTIONS);
    if (!user.userId) return null;

    return {
      // HS-1292: `/Auth/me` now reports whether this session is still on a temporary password.
      // The mapper copies it only when the backend actually said so — see `restore` below for
      // why an absent field is not the same as `false`.
      ...(typeof user.requiresPasswordChange === 'boolean'
        ? { requiresPasswordChange: user.requiresPasswordChange }
        : {}),
      user: {
        userId: user.userId,
        ...(user.institutionId ? { institutionId: user.institutionId } : {}),
        ...(user.accountId ? { accountId: user.accountId } : {}),
        ...(user.email ? { email: user.email } : {}),
        ...(user.login ? { login: user.login } : {}),
        ...(user.firstName ? { firstName: user.firstName } : {}),
        ...(user.lastName ? { lastName: user.lastName } : {}),
      },
      // Authorization is evaluated on effective permissions, never on a role.
      permissions: user.permissions ?? [],
      // HS-980: the caller's role names choose the shell (Installer workspace or admin) and
      // nothing else. HS-1617 adds them to `/Auth/me`; the generated `CurrentUserInfo` predates
      // that, so they are read defensively and an absent field maps to `[]`.
      roles: reportedRoles(user),
      // HS-1387: carried only when reported. `undefined` is what makes an empty permission list
      // readable as genuine rather than withheld — see `permissionsReported` in the package.
      ...(user.sessionId ? { sessionId: user.sessionId } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Build a client with admin's configuration.
 *
 * The app uses exactly one — `authClient` below. The factory exists so a test can build a
 * SECOND, fresh one: that is what a browser reload looks like from the session's point of view,
 * and the reload path is the one HS-1225 had to close.
 */
export function createAdminAuthClient() {
  const client = createAuth({
    app: 'admin',
    // `oauth-redirect` = AWS Cognito through the backend `OAuthController` (Q-09): the FE asks for
    // the Hosted UI URL and redirects, the backend owns the code exchange and creates the session.
    strategies: ['credentials', 'oauth-redirect'],
    // Admin authenticates with HttpOnly cookies. Pin the mode: the default auto-detects
    // `bearer` when the app runs standalone (installed PWA), and because `signIn` is overridden
    // it never calls `tokenStore.setTokens` — so `getAuthHeader()` would return null and every
    // request would silently lose auth.
    tokenStore: 'cookie',
    // No `mfa` route (HS-859 U5): MFA is backend logic whose shapes are still unrecorded (Q-04)
    // and spec 0.6.0.0 exposes no `/api/Auth/mfa/*`, so admin ships without one and the guard's
    // `mfaRequired` branch falls through to `login`.
    // HS-1225: `changePassword` used to point back at `/login`, because no such screen existed —
    // which made every newly created account a dead end (its temporary password forces the flow).
    routes: {
      login: '/login',
      changePassword: '/change-password',
      oauthCallback: '/auth/callback',
      // HS-1408: where the guard sends a signed-in session that lands on a challenge screen. The
      // same destination `safeDestination` already falls back to.
      home: '/dashboard',
    },
    /**
     * HS-1387: one place every 403 is written down. Before this the only trace of a refused
     * request was whatever the screen that made it happened to render, which for a background
     * query was nothing at all — the audit's "no central logging" finding.
     *
     * The endpoint is named because it is the whole diagnostic value: "forbidden" alone does not
     * say whether the user hit a screen they may not open or a control the UI should have hidden.
     */
    reportForbidden: ({ url }) => {
      logger.warn(`403 Forbidden from ${url ?? 'an unnamed endpoint'}.`);
    },
    operations: {
      /**
       * Every exit must be a resolved `AuthResult`, never a rejection.
       *
       * `validateStatus` accepts only 200 and 301, so a 401 — a wrong password, the most common
       * outcome after success — rejects. If that rejection escapes, the package cannot dispatch
       * the resolving `SIGN_IN` event and the machine is stranded in `authenticating`: the login
       * button spins forever and the error is never shown, because the form reads it off
       * `unauthenticated`. The package's own default operations wrap the call for exactly this
       * reason; the 301 override has to do the same.
       */
      async signIn({ email, password }) {
        try {
          // HS-1584: the backend refuses a login without its channel (`400 LOGIN_CHANNEL_REQUIRED`).
          const result = await postApiAuthLogin(
            { email, password, loginChannel: LOGIN_CHANNEL.admin },
            LOGIN_REQUEST_OPTIONS,
          );
          // HS-1225: the flag is written on BOTH branches. Written only on the challenge branch it
          // would outlive the flow — the next person to sign in on this tab with a settled password
          // would be sent to the change screen for a password they already chose.
          tokenManager.setPasswordChangeRequired(result.requiresPasswordChange === true);
          if (result.requiresPasswordChange === true) {
            return { outcome: 'passwordChangeRequired', flowToken: PASSWORD_CHANGE_FLOW_TOKEN };
          }
          const session = await restoreSession();
          return session
            ? { outcome: 'authenticated', session }
            : { outcome: 'failed', error: { messageKey: 'errors.unauthorized' } };
        } catch (error) {
          // HS-1584: `authErrorFromThrown` keeps the backend's `errorCode`, which is what tells a
          // 403 `MISSING_PERMISSION` apart from any other refusal on the login screen.
          return { outcome: 'failed', error: authErrorFromThrown(error) };
        }
      },
      /**
       * Resolve only when the SERVER session is really gone; reject otherwise.
       *
       * The session is an HttpOnly cookie, which JavaScript cannot delete. If this request never
       * landed, the cookie survives — so clearing the local half would make the two diverge, and
       * `client.signOut()`'s `resetReady()` re-arms `restore()`: the next navigation probes
       * `/Auth/me`, succeeds on the surviving cookie and signs the user straight back in. Asking
       * to leave would deposit them on the dashboard.
       *
       * A rejection keeps the package from touching local state, so "signed in" stays true as
       * long as it *is* true. The caller surfaces the failure.
       */
      async signOut() {
        try {
          await postApiAuthLogout({ skipAuthRefresh: true });
        } catch (error) {
          // 401 is the one failure that means success: the cookie is already invalid, so there is
          // no server session left to end. Reporting an error would tell a signed-out user that
          // signing out failed.
          const status = error instanceof NormalizedApiError ? error.status : undefined;
          if (status !== STATUS_UNAUTHORIZED) throw error;
        }
        // Only once the server session is really gone. Clearing earlier would drop the flag while
        // the cookie that makes it true is still live.
        tokenManager.clearTokens();
      },
      /**
       * HS-1225: the package ships a stub here that always fails (`unsupportedFlow`), because the
       * wire contract was unrecorded. It is recorded now — `PUT /api/User/change-password` takes
       * `{ oldPassword, newPassword }`, field for field the package's `ChangePasswordInput` — so
       * admin supplies the real implementation through the same injectable seam the 301 login uses.
       *
       * The request rides the cookie the 301 login already set, so it is authenticated even though
       * the session is not yet usable. On success the backend clears `RequiresPasswordChange`, and
       * re-reading `/Auth/me` promotes the machine straight to `authenticated` — the user does not
       * sign in twice.
       *
       * `flowToken` is unused: admin's flow is carried by the cookie, not by a token. It is in the
       * signature because the package passes it.
       *
       * Never rejects, for the reason spelled out on `signIn`: an escaping rejection strands the
       * machine mid-transition.
       */
      async changePassword({ oldPassword, newPassword }) {
        try {
          await putApiUserChangePassword({ oldPassword, newPassword });
          // The change landed. Whatever happens next, this session is no longer on a temporary
          // password, so the flag goes first — leaving it set would send the user back here.
          tokenManager.setPasswordChangeRequired(false);
          const session = await restoreSession();
          if (session) return { outcome: 'authenticated', session };
          // HS-1225: the password WAS changed and `/Auth/me` still says no session — the backend
          // rotated or dropped the cookie on the change. Reporting `errors.unauthorized` here used
          // to render as "Incorrect username or password", telling the user the opposite of what
          // happened; and because a rejected step now returns to its own challenge, it would also
          // park them on a form asking for a temporary password that no longer exists. `endsFlow`
          // is what says "do not offer a retry" — the screen sends them to sign in with the
          // password they just chose.
          return {
            outcome: 'failed',
            error: { messageKey: 'auth.error.passwordChangedSignInAgain', endsFlow: true },
          };
        } catch (error) {
          // HS-1405: keep the backend's `errorCode`. A 400 here is either a refused new password
          // (`ValidationError`) or a wrong temporary one (`InvalidCurrentPassword`), and the
          // status alone cannot tell the screen which of the two to say.
          return { outcome: 'failed', error: authErrorFromThrown(error) };
        }
      },
      /**
       * HS-1225 / HS-1292: re-assert the forced password change across a reload — and across a
       * second tab, which the remembered flag alone could never do.
       *
       * The login sets the session cookie before the password is changed, so `/Auth/me` answers
       * happily on it. The challenge itself lives only in memory, which meant a reload on
       * `/change-password` settled the machine on `authenticated` and let the user into the panel
       * still holding the temporary password. That was reachable by accident, not just by
       * curiosity: `bootstrapChunkReload` reloads the page by itself when a route chunk fails.
       *
       * The server now answers the question (HS-1292), so `session.requiresPasswordChange` is the
       * authority and `sessionStorage` is demoted to a fallback for the one case the server does
       * not cover: a deployment that predates the fix, which omits the field entirely. Reading
       * that silence as `false` would put a temporary-password session into the panel, which is
       * the hole this whole ticket is about.
       *
       * Whatever the server says is also written back, so the stored copy cannot outlive the fact
       * it describes — a password settled in one tab no longer sends the next sign-in on another
       * tab to the change screen.
       */
      async restore() {
        const session = await restoreSession();
        const reported = session?.requiresPasswordChange;
        if (typeof reported === 'boolean') tokenManager.setPasswordChangeRequired(reported);
        const required = reported ?? tokenManager.requiresPasswordChange();
        if (!required) return session;
        if (!session) {
          // The cookie is gone, so the flag describes nothing. Drop it rather than stranding the
          // next sign-in on this tab.
          tokenManager.setPasswordChangeRequired(false);
          return null;
        }
        // Re-enter the challenge BEFORE the package folds this session in: `SESSION_RESTORED` only
        // applies to an `unauthenticated` machine, so the restored session is ignored and the guard
        // routes the user back to `/change-password`.
        client.requirePasswordChange(PASSWORD_CHANGE_FLOW_TOKEN);
        return session;
      },
    },
  });

  return client;
}

export const authClient = createAdminAuthClient();
